import React from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, Loader2, Clock, FileText, RefreshCw, X } from 'lucide-react';

import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
} from '../../Navbar/modals/changePasswordModal.styles';

import {
  UploadList,
  UploadListItem,
  UploadItemIcon,
  UploadItemName,
  UploadItemStatus,
} from './UploadProgressModal.styles';
import {
  IconButton,
  ProgressBarTrack,
  ProgressBarFill,
} from '../../UploadDocuments/uploadDocuments.styles';
import FolderProgressBar from '../../UploadDocuments/components/FolderProgressBar';

// status -> { label, icon, color, spin }
const STATUS_CONFIG = {
  pending: { label: 'Waiting', icon: Clock, color: '#94a3b8', spin: false },
  uploading: { label: 'Uploading', icon: Loader2, color: '#3b82f6', spin: true },
  done: { label: 'Done', icon: CheckCircle2, color: '#2f9e5b', spin: false },
  error: { label: 'Failed', icon: XCircle, color: '#e5484d', spin: false },
  skipped: {
    label: 'Skipped',
    icon: XCircle,
    color: '#94a3b8',
    spin: false,
  },
  'size-error': {
    label: 'File Too Large',
    icon: XCircle,
    color: '#e5484d',
    spin: false,
  },
};

const UploadItemRow = ({ item, onRetry, onSkip, isAnyUploading }) => {
  const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const StatusIcon = config.icon;

  return (
    <UploadListItem style={{ flexWrap: 'wrap' }}>
      <UploadItemIcon>
        <FileText size={16} />
      </UploadItemIcon>

      <UploadItemName title={item.name}>{item.name}</UploadItemName>

      <UploadItemStatus style={{ color: config.color }}>
        <StatusIcon size={14} className={config.spin ? 'spin' : undefined} />
        {config.label}
        {item.status === 'uploading' ? ` ${item.progress ?? 0}%` : null}
      </UploadItemStatus>

      {item.status === 'uploading' && (
        <ProgressBarTrack
          aria-label={`Upload progress for ${item.name}`}
          style={{ flexBasis: '100%' }}
        >
          <ProgressBarFill style={{ width: `${item.progress ?? 0}%` }} />
        </ProgressBarTrack>
      )}

      {(item.status === 'error' || item.status === 'size-error') && (
        <>
          {item.status === 'error' && (
            <IconButton
              type="button"
              onClick={() => onRetry(item.id)}
              disabled={isAnyUploading}
              title="Retry upload"
              aria-label={`Retry upload for ${item.name}`}
            >
              <RefreshCw size={14} />
            </IconButton>
          )}

          <IconButton
            type="button"
            onClick={() => onSkip(item.id)}
            disabled={isAnyUploading}
            title="Skip upload"
            aria-label={`Skip upload for ${item.name}`}
          >
            Skip
          </IconButton>
        </>
      )}
    </UploadListItem>
  );
};

// items: [{ id, name, status, progress, rootFolder?, isFolderUpload? }]
// Items sharing the same rootFolder (with isFolderUpload: true) are grouped
// under one FolderProgressBar, matching the same aggregate shown next to
// "Uploaded Documents" in UploadDocuments.jsx. Items without a folder (or
// from "Browse Files"/lone drops) render individually, unchanged.
// onRetry(itemId) re-uploads only that one file; onClose is disabled while
// any item is actively uploading so the user can't dismiss mid-flight.
const UploadProgressModal = ({ isOpen, items, onClose, onRetry, onSkip }) => {
  if (!isOpen) return null;

  const total = items.length;
  const completed = items.filter(
    (item) => item.status === 'done' || item.status === 'skipped'
  ).length;
  const isAnyUploading = items.some((item) => item.status === 'uploading');

  // Split into folder-grouped items vs standalone items, preserving order
  // of first appearance for both groups and standalone items.
  const folderOrder = [];
  const folderMap = {};
  const standalone = [];

  items.forEach((item) => {
    if (item.isFolderUpload && item.rootFolder) {
      if (!folderMap[item.rootFolder]) {
        folderMap[item.rootFolder] = [];
        folderOrder.push(item.rootFolder);
      }
      folderMap[item.rootFolder].push(item);
    } else {
      standalone.push(item);
    }
  });

  return createPortal(
    <ModalOverlay
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-progress-title"
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="upload-progress-title">
            Uploading files ({completed}/{total})
          </ModalTitle>
          <ModalCloseButton
            onClick={onClose}
            type="button"
            aria-label="Close"
            disabled={isAnyUploading}
          >
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <UploadList>
            {folderOrder.map((rootFolder) => {
              const folderItems = folderMap[rootFolder];
              return (
                <div key={rootFolder} style={{ marginBottom: 12 }}>
                  <FolderProgressBar
                    rootFolder={rootFolder}
                    docs={folderItems}
                    style={{ minWidth: 0, maxWidth: 'none', marginBottom: 6 }}
                  />

                  {folderItems.map((item) => (
                    <UploadItemRow
                      key={item.id}
                      item={item}
                      onRetry={onRetry}
                      onSkip={onSkip}
                      isAnyUploading={isAnyUploading}
                    />
                  ))}
                </div>
              );
            })}

            {standalone.map((item) => (
              <UploadItemRow
                key={item.id}
                item={item}
                onRetry={onRetry}
                onSkip={onSkip}
                isAnyUploading={isAnyUploading}
              />
            ))}
          </UploadList>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default UploadProgressModal;
